import React from 'react';
import { ClientIntelligence } from '../../types/clientIntelligence';
import { ConfidenceMeter, PriorityBubble } from './VisualComponents';

interface IntelligenceProfileProps {
  intelligence: ClientIntelligence;
}

export const IntelligenceProfile: React.FC<IntelligenceProfileProps> = ({ intelligence }) => {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200 p-6 h-auto max-h-[500px] overflow-y-auto">
      <h3 className="font-semibold text-purple-800 mb-4 flex items-center gap-2 sticky top-0 bg-gradient-to-br from-purple-50 to-indigo-50 pb-2 border-b border-purple-200">
        🧠 Client Intelligence Profile
      </h3>
      
      <ConfidenceMeter confidence={intelligence.profile.metrics.aiConfidence} gradient={{ from: '#a855f7', to: '#4338ca' }} />
      
      <div className="space-y-4">
        {/* Communication Style */}
        <div className="bg-white rounded-lg p-4 border border-purple-100 hover:shadow-md transition-shadow">
          <h4 className="font-medium text-sm text-purple-700 mb-3 flex items-center gap-2">
            💬 Communication Style
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
              {intelligence.profile.metrics.communicationMetrics.dominantStyle}
            </span>
          </h4>
          <div className="text-sm text-purple-600 mb-4">
            {intelligence.profile.communicationStyle}
          </div>
          
          {/* Real Communication Style Indicators */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Technical Level</span>
                <span>{intelligence.profile.metrics.communicationMetrics.technicalLevel}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${intelligence.profile.metrics.communicationMetrics.technicalLevel}%` }}></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Detail-Oriented</span>
                <span>{intelligence.profile.metrics.communicationMetrics.detailOrientation}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${intelligence.profile.metrics.communicationMetrics.detailOrientation}%` }}></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Formal Tone</span>
                <span>{intelligence.profile.metrics.communicationMetrics.formalityLevel}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-400 h-2 rounded-full" style={{ width: `${intelligence.profile.metrics.communicationMetrics.formalityLevel}%` }}></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span>Efficiency Focus</span>
                <span>{intelligence.profile.metrics.communicationMetrics.efficiencyFocus}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${intelligence.profile.metrics.communicationMetrics.efficiencyFocus}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Priorities */}
        <div className="bg-white rounded-lg p-4 border border-purple-100 hover:shadow-md transition-shadow">
          <h4 className="font-medium text-sm text-purple-700 mb-3 flex items-center gap-2">
            🎯 Key Priorities
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">{intelligence.profile.metrics.priorityAnalysis.length} Identified</span>
          </h4>
          <div className="text-sm text-purple-600 mb-4">
            {intelligence.profile.keyPriorities}
          </div>
          
          {/* Real Priority Bubbles from Analysis */}
          <div className="space-y-2">
            {intelligence.profile.metrics.priorityAnalysis.map((priority, index) => {
              const colors = [
                { bg: '#b91c1c', class: 'bg-red-700' },    // Red 700
                { bg: '#c2410c', class: 'bg-orange-700' }, // Orange 700  
                { bg: '#7c3aed', class: 'bg-purple-700' }, // Purple 700
                { bg: '#15803d', class: 'bg-green-700' },  // Green 700
                { bg: '#1d4ed8', class: 'bg-blue-700' },   // Blue 700
                { bg: '#4338ca', class: 'bg-indigo-700' }, // Indigo 700
                { bg: '#be185d', class: 'bg-pink-700' },   // Pink 700
                { bg: '#374151', class: 'bg-gray-700' }    // Gray 700
              ];
              const colorObj = colors[index % colors.length];
              return (
                <div key={priority.priority} className="flex items-center gap-3 p-2">
                  <div 
                    className={`rounded-full flex items-center justify-center text-white font-bold text-xs shadow-md`}
                    style={{ 
                      width: `${Math.max(40, Math.min(80, priority.score))}px`, 
                      height: `${Math.max(40, Math.min(80, priority.score))}px`,
                      backgroundColor: colorObj.bg
                    }}
                  >
                    {priority.score}
                  </div>
                  <span className="text-sm text-gray-700 flex-1">{priority.priority}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Decision Making */}
        <div className="bg-white rounded-lg p-4 border border-purple-100 hover:shadow-md transition-shadow">
          <h4 className="font-medium text-sm text-purple-700 mb-3 flex items-center gap-2">
            🤔 Decision Making
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">{intelligence.profile.metrics.decisionMakingProfile.style}</span>
          </h4>
          <div className="text-sm text-purple-600 mb-4">
            {intelligence.profile.decisionMaking}
          </div>
          
          {/* Decision Process Flow */}
          <div className="flex items-center justify-between text-xs">
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">1</div>
              <div>Research</div>
            </div>
            <div className="w-6 h-0.5 bg-purple-300"></div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">2</div>
              <div>Analyze</div>
            </div>
            <div className="w-6 h-0.5 bg-purple-300"></div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">3</div>
              <div>Consult</div>
            </div>
            <div className="w-6 h-0.5 bg-purple-300"></div>
            <div className="text-center space-y-1">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">✓</div>
              <div>Decide</div>
            </div>
          </div>
          
          {/* Real Decision Traits */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="bg-purple-50 p-2 rounded text-center">
              <div className="text-lg font-bold text-purple-700">{intelligence.profile.metrics.decisionMakingProfile.avgDecisionTime}</div>
              <div className="text-xs text-purple-600">Avg Decision Time</div>
            </div>
            <div className="bg-indigo-50 p-2 rounded text-center">
              <div className="text-lg font-bold text-indigo-700 capitalize">{intelligence.profile.metrics.decisionMakingProfile.riskTolerance}</div>
              <div className="text-xs text-indigo-600">Risk Tolerance</div>
            </div>
          </div>
        </div>

        {/* Personality Insights */}
        <div className="bg-white rounded-lg p-4 border border-purple-100 hover:shadow-md transition-shadow">
          <h4 className="font-medium text-sm text-purple-700 mb-3 flex items-center gap-2">
            ✨ Personality Insights
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">{intelligence.profile.metrics.personalityTraits.personalityType}</span>
          </h4>
          <div className="text-sm text-purple-600 mb-4">
            {intelligence.profile.personalityInsights}
          </div>
          
          {/* Real Personality Traits */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gradient-to-r from-purple-100 to-purple-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="text-xs font-medium">Leadership</span>
              </div>
              <div className="text-sm font-bold text-purple-700">{intelligence.profile.metrics.personalityTraits.leadership}%</div>
            </div>
            <div className="bg-gradient-to-r from-indigo-100 to-indigo-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
                <span className="text-xs font-medium">Innovation</span>
              </div>
              <div className="text-sm font-bold text-indigo-700">{intelligence.profile.metrics.personalityTraits.innovation}%</div>
            </div>
            <div className="bg-gradient-to-r from-blue-100 to-blue-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-xs font-medium">Patience</span>
              </div>
              <div className="text-sm font-bold text-blue-700">{intelligence.profile.metrics.personalityTraits.patience}%</div>
            </div>
            <div className="bg-gradient-to-r from-green-100 to-green-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-xs font-medium">Trust</span>
              </div>
              <div className="text-sm font-bold text-green-700">{intelligence.profile.metrics.personalityTraits.trust}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 